'use client';
import React, { useCallback, useMemo, useState } from 'react';
import { toastr } from 'react-redux-toastr';

import { useMutation, useLazyQuery, ApolloError } from '@apollo/client';

import { Box } from '@nerdwallet/currency/components/Box';
import { CheckboxOption } from '@nerdwallet/currency/components/CheckboxOption';
import { InputSSN } from '@nerdwallet/currency/components/InputSSN';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import { Type } from '@nerdwallet/currency/components/Type';

import {
  INITIALIZE_CREDIT_PROFILE_MUTATION,
  TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
} from '@components/transunion-flow/steps/queries';

import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';

import {
  getDuplicateEmailFromError,
  isTUBlocked,
} from '@components/transunion-flow/utils';

import FriendlyTitle from '../components/friendly-title';
import ContinueButton from '../components/continue-button';

import { CURRENT_TU_TERMS_OF_SERVICE } from '../constants';

import { getCopy } from '@lib/strings';
import {
  generateCreateTUCustomerErrorMessage,
  isFullSsnValid,
  isSsnNumbersMatching,
} from '@components/transunion-flow/utils';
import { SegmentPageView } from '@lib/segment';
import { CreditProfileAuthStatusProfile } from '@generated-gql/generated-types';

interface FullSSNFormState {
  fullSSN: string;
  confirmSSN: string;
  agree: boolean;
  validation: {
    fullSSN?: {
      isValid: boolean;
      feedback: string | null;
    };
    confirmSSN?: {
      isValid: boolean;
      feedback: string | null;
    };
  };
}

interface Props {
  onInitializeSuccess: (profile: CreditProfileAuthStatusProfile) => void;
}

export const FullSSNStep = ({ onInitializeSuccess }: Props) => {
  const { setTuVisibility } = useTuVisibilityContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    tuCustomerInfo,
    setTuCustomerInfo,
    setAuthStatusProfile,
    setTuErrors,
  } = useTuStepper();

  const [getAuthenticationStatusProfile] = useLazyQuery(
    TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
  );

  const handleCreateTuCustomerSuccess = (
    data: CreditProfileAuthStatusProfile,
  ) => {
    onInitializeSuccess(data);
    setIsLoading(false);
  };

  const handleCreateTuCustomerFail = async (createError: ApolloError) => {
    // Retrieve authentication status profile
    const { data, error } = await getAuthenticationStatusProfile({
      fetchPolicy: 'network-only',
    });
    setIsLoading(false);
    if (data && !error) {
      const tasp = data.tuAuthenticationStatusProfile;
      setAuthStatusProfile(tasp);

      const errorMsg = generateCreateTUCustomerErrorMessage(createError);
      if (errorMsg) {
        toastr.error('', errorMsg);
      }

      const duplicateEmail = getDuplicateEmailFromError(createError);
      if (isTUBlocked(tasp) || duplicateEmail) {
        if (duplicateEmail) {
          // if the user is detected to have an active TU profile already with another email
          setTuErrors({
            duplicateEmail,
          });
        }
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_BLOCKED,
        });
      }
    }
  };

  const [createTUCustomer] = useMutation(INITIALIZE_CREDIT_PROFILE_MUTATION, {
    onCompleted: (data) => {
      handleCreateTuCustomerSuccess(data.initializeCreditProfile);
    },
    onError: handleCreateTuCustomerFail,
  });

  const [fullSSNForm, setFullSSNForm] = useState<FullSSNFormState>({
    fullSSN: '',
    confirmSSN: '',
    agree: false,
    validation: {
      fullSSN: {
        isValid: null,
        feedback: null,
      },
      confirmSSN: {
        isValid: null,
        feedback: null,
      },
    },
  });

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const { fullSSN, agree, validation } = fullSSNForm;

      const isSSNComplete = isFullSsnValid(fullSSN);

      if (
        !isSSNComplete ||
        !tuCustomerInfo ||
        !agree ||
        !validation.fullSSN.isValid ||
        !validation.confirmSSN.isValid
      ) {
        return;
      }

      const ssnData = {
        ...tuCustomerInfo,
        // remove partial SSN since we're sending the full SSN
        partialSsn: undefined,
        fullSsn: fullSSN,
      };

      setTuCustomerInfo({ ...ssnData });

      setIsLoading(true);
      await createTUCustomer({
        variables: {
          customer: ssnData,
        },
      });
    },
    [fullSSNForm, tuCustomerInfo, setTuCustomerInfo, createTUCustomer],
  );

  const onChangeSSN = useCallback(
    (e, field) => {
      let feedback;

      const finalValue = e.target.value.replace(/[ -]/g, '');
      const otherField = field === 'fullSSN' ? 'confirmSSN' : 'fullSSN';
      const otherFieldValue = fullSSNForm[otherField];
      const isConfirmed = isSsnNumbersMatching(otherFieldValue, finalValue);

      const isValid = (value) =>
        isFullSsnValid(value) && isFullSsnValid(otherFieldValue)
          ? isConfirmed
          : true;
      const feedbackMessage = (value) =>
        isValid(value) ? undefined : feedback;

      if (!isConfirmed) {
        feedback = 'SSN numbers must match';
      }

      setFullSSNForm((prevState) => ({
        ...prevState,
        [field]: finalValue,
        validation: {
          [field]: {
            isValid: isValid(finalValue),
            feedback: feedbackMessage(finalValue),
          },
          [otherField]: {
            isValid: isValid(otherFieldValue),
            feedback: feedbackMessage(otherFieldValue),
          },
        },
      }));
    },
    [fullSSNForm],
  );

  const renderInput = useCallback(
    (data) => {
      const { label, value, key } = data;
      return (
        <InputSSN
          error={fullSSNForm.validation[key].isValid === false}
          errorMessage={fullSSNForm.validation[key]?.feedback}
          label={label}
          fullWidth
          value={value}
          onChange={(e) => onChangeSSN(e, key)}
          type="tel"
          name={key}
        />
      );
    },
    [fullSSNForm, onChangeSSN],
  );
  const handleCheckboxChange = useCallback((e, key) => {
    e.persist();
    setFullSSNForm((prevState) => ({
      ...prevState,
      [key]: e.target.checked,
    }));
  }, []);

  const isDisabled = useMemo(
    () =>
      !fullSSNForm.agree ||
      !fullSSNForm.validation.confirmSSN.isValid ||
      !fullSSNForm.validation.fullSSN.isValid,
    [fullSSNForm],
  );

  return (
    <SegmentPageView name="tu_full_ssn">
      <form onSubmit={onSubmit}>
        <FriendlyTitle
          title={getCopy('creditScoreFullSSNHeading')}
          subtext={getCopy('creditScoreFullSSNSubHeading')}
        />

        <Box marginTop={2}>
          {renderInput({
            label: 'Enter your Social Security number',
            value: fullSSNForm.fullSSN,
            key: 'fullSSN',
          })}
        </Box>

        <Box marginTop={2} marginBottom={4}>
          {renderInput({
            label: 'Confirm your Social Security number',
            value: fullSSNForm.confirmSSN,
            key: 'confirmSSN',
          })}
        </Box>

        <CheckboxOption
          checked={fullSSNForm.agree}
          inline
          label="I agree to the terms below"
          onChange={(e) => handleCheckboxChange(e, 'agree')}
        />
        <Type size={1} color="neutral-light">
          {CURRENT_TU_TERMS_OF_SERVICE}
        </Type>

        <Box marginTop={4}>
          <ContinueButton isDisabled={isDisabled} isLoading={isLoading} />
        </Box>
        <Box marginTop={4}>
          <SecureEncryptionMessage />
        </Box>
      </form>
    </SegmentPageView>
  );
};

export default FullSSNStep;
